import React, { useState, useEffect, useCallback } from "react"
import { Loader2, Send, Plus, Trash2, MessageCircle, Mail, Eye, Clock } from "lucide-react"
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter,
} from "src/components/ui/sheet"
import { Button } from "src/components/ui/button"
import { Input } from "src/components/ui/input"
import { Textarea } from "src/components/ui/textarea"
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "src/components/ui/select"
import { useToast } from "src/components/ui/toast"
import { shareDocument, fetchDocumentShares } from "../api/documentsApi"

const CHANNEL_ICON = { whatsapp: MessageCircle, email: Mail }

function blankRecipient() {
  return { channel: "whatsapp", to: "", name: "" }
}

function formatWhen(iso) {
  if (!iso) return ""
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" })
}

// Drawer to share a single document with one or more recipients over WhatsApp
// or email, plus the share history (audit trail). `document` is the doc row.
export function ShareDocumentDrawer({ document, open, onOpenChange }) {
  const { toast } = useToast()
  const [recipients, setRecipients] = useState([blankRecipient()])
  const [message, setMessage] = useState("")
  const [sending, setSending] = useState(false)
  const [history, setHistory] = useState(null)

  const loadHistory = useCallback(() => {
    if (!document) return
    fetchDocumentShares(document.id).then(setHistory).catch(() => setHistory([]))
  }, [document])

  useEffect(() => {
    if (open) {
      setRecipients([blankRecipient()])
      setMessage("")
      loadHistory()
    }
  }, [open, loadHistory])

  function updateRecipient(i, patch) {
    setRecipients((cur) => cur.map((r, idx) => (idx === i ? { ...r, ...patch } : r)))
  }

  const notShareable = document && !document.is_shareable_with_customer

  async function handleSend() {
    const valid = recipients.filter((r) => r.to.trim())
    if (valid.length === 0) {
      toast({ variant: "destructive", title: "Add at least one recipient" })
      return
    }
    setSending(true)
    try {
      await shareDocument(document.id, { recipients: valid, message: message.trim() || undefined })
      toast({ title: valid.length > 1 ? `Shared with ${valid.length} recipients` : "Document shared" })
      setRecipients([blankRecipient()])
      setMessage("")
      loadHistory()
    } catch (err) {
      toast({ variant: "destructive", title: "Could not share", description: err.response?.data?.errors?.[0] })
    } finally {
      setSending(false)
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex flex-col p-0 sm:max-w-[480px]">
        <SheetHeader>
          <SheetTitle>Share document</SheetTitle>
          <SheetDescription>
            {document?.title || document?.file_name} · a private, expiring link is sent to each recipient.
          </SheetDescription>
        </SheetHeader>

        {notShareable ? (
          <div className="flex-1 overflow-y-auto px-6 py-5">
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              This document isn't marked shareable with customers. Edit the document and enable
              “Shareable with customer” before sending it.
            </div>
          </div>
        ) : (
          <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
            {/* Recipients */}
            <div className="space-y-3">
              {recipients.map((r, i) => {
                const Icon = CHANNEL_ICON[r.channel]
                return (
                  <div key={i} className="rounded-xl border border-slate-200 p-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <Select value={r.channel} onValueChange={(v) => updateRecipient(i, { channel: v })}>
                        <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="whatsapp">WhatsApp</SelectItem>
                          <SelectItem value="email">Email</SelectItem>
                        </SelectContent>
                      </Select>
                      <Icon className="h-4 w-4 text-slate-400" />
                      {recipients.length > 1 && (
                        <button
                          onClick={() => setRecipients((cur) => cur.filter((_, idx) => idx !== i))}
                          className="ml-auto flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                    <Input
                      value={r.to}
                      onChange={(e) => updateRecipient(i, { to: e.target.value })}
                      placeholder={r.channel === "email" ? "buyer@example.com" : "9876543210"}
                    />
                    <Input
                      value={r.name}
                      onChange={(e) => updateRecipient(i, { name: e.target.value })}
                      placeholder="Recipient name (optional)"
                    />
                  </div>
                )
              })}
              <Button
                variant="outline" size="sm" className="gap-1.5"
                onClick={() => setRecipients((cur) => [...cur, blankRecipient()])}
              >
                <Plus className="h-3.5 w-3.5" /> Add recipient
              </Button>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium text-slate-700">Message (optional)</label>
              <Textarea
                value={message} onChange={(e) => setMessage(e.target.value)} rows={3}
                placeholder="Add a short note for the recipient…"
              />
            </div>

            {/* History */}
            {history && history.length > 0 && (
              <div className="space-y-2 border-t border-slate-100 pt-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Share history</div>
                <ul className="space-y-1.5">
                  {history.map((s) => {
                    const Icon = CHANNEL_ICON[s.channel] || Mail
                    return (
                      <li key={s.id} className="flex items-center gap-2 text-[13px] text-slate-600">
                        <Icon className="h-3.5 w-3.5 text-slate-400" />
                        <span className="truncate">{s.recipient}</span>
                        <span className="ml-auto flex items-center gap-2 text-[11px] text-slate-400">
                          {s.viewed_at ? (
                            <span className="flex items-center gap-0.5"><Eye className="h-3 w-3" />{s.view_count}</span>
                          ) : (
                            <span className="flex items-center gap-0.5"><Clock className="h-3 w-3" />sent</span>
                          )}
                          {!s.active && <span className="text-red-400">expired</span>}
                          {formatWhen(s.created_at)}
                        </span>
                      </li>
                    )
                  })}
                </ul>
              </div>
            )}
          </div>
        )}

        <SheetFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          {!notShareable && (
            <Button onClick={handleSend} disabled={sending} className="gap-1.5">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {sending ? "Sending…" : "Send"}
            </Button>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
